import type { RequestConfig } from "@app/config/axios/http";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import { divisionApi, type ApiDivision } from "@src/api/dictionaries/divisions";
import { measureApi, type ApiMeasure } from "@src/api/dictionaries/measures";
import { ordersApi, type ApiOrder } from "@src/api/orders";
import { stockDepotCardsApi, type ApiStockDepotCard } from "@src/api/stock/stockDepotCards";
import { stockObjsApi, type StockObj } from "@src/api/stock/stockObjs";
import { lotsServices, type ApiStockLot } from "../lots";
import { storePlacesServices, type StorePlaceRemainder } from "../storePlaces";
import { stockActInvsApi } from "./api.core";
import type {
    ApiStockActInvContentData,
    ApiStockActInvData,
    StockActInv,
    StockActInvContent,
    StockActInvContentExtends,
    StockActInvContentFilter,
    StockActInvContentUpdateFactQtyData,
    StockActInvExtends,
    StockActInvFilter,
} from "./types";

/** Сервисный слой — АИ СУ для полной инвентаризации склада. */
export const stockActInvsServices = {
    ...stockActInvsApi,

    /**
     * Загружает АИ СУ и при необходимости догружает склад и состояние BO.
     * @param params фильтр и include
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            filter?: StockActInvFilter;
            include?: Array<keyof StockActInvExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockActInv[]> => {
        const { filter, include } = params;
        const acts = await stockActInvsApi.load({ flt: filter }, config);

        if (include == null) {
            return acts.map((act) => serializeAct(act));
        }

        const includeDivision = include.includes("Division");
        const includeState = include.includes("State");
        const divisionCodes = new Set<number>();
        const stateCodes = new Set<number>();

        acts.forEach((act) => {
            if (includeDivision) addValidCode(divisionCodes, act.DivisionCode);
            if (includeState) addValidCode(stateCodes, act.Bo?.State);
        });

        const divisions = new Map<number, ApiDivision>();
        const states = new Map<number, ApiOmpBoState>();
        const promises: Promise<unknown>[] = [];

        if (divisionCodes.size > 0) {
            promises.push(
                divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config).then(({ items }) => {
                    items.forEach((item) => divisions.set(item.Code, item));
                }),
            );
        }

        if (stateCodes.size > 0) {
            promises.push(
                boApi.states.load({ Codes: [...stateCodes] }, config).then((items) => {
                    items.forEach((item) => states.set(item.Code, item));
                }),
            );
        }

        await Promise.all(promises);

        return acts.map((act) =>
            serializeAct(act, {
                Division: includeDivision ? divisions.get(act.DivisionCode) : undefined,
                State: includeState ? states.get(act.Bo?.State ?? 0) : undefined,
            }),
        );
    },

    /**
     * Возвращает один АИ СУ по коду.
     * @param params код АИ и include
     * @param config опции HTTP-запроса
     */
    loadByCode: async (
        params: {
            code: number;
            include?: Array<keyof StockActInvExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockActInv | null> => {
        const acts = await stockActInvsServices.load(
            {
                filter: { Codes: [params.code] },
                include: params.include,
            },
            config,
        );

        return acts[0] ?? null;
    },

    /** Состав АИ СУ. */
    content: {
        ...stockActInvsApi.content,

        /**
         * Загружает строки состава АИ и связанные справочники.
         * @param params фильтр и include состава
         * @param config опции HTTP-запроса
         */
        load: async (
            params: {
                filter?: StockActInvContentFilter;
                include?: Array<keyof StockActInvContentExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockActInvContent[]> => {
            const { filter, include } = params;
            const contents = await stockActInvsApi.content.load({ flt: filter }, config);

            if (include == null) {
                return contents.map((content) => serializeContent(content));
            }

            return enrichContents(contents, include, config);
        },

        /**
         * Возвращает одну строку состава АИ по коду.
         * @param params код строки и include
         * @param config опции HTTP-запроса
         */
        loadByCode: async (
            params: {
                code: number;
                actCode?: number;
                include?: Array<keyof StockActInvContentExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockActInvContent | null> => {
            const contents = await stockActInvsServices.content.load(
                {
                    filter: {
                        Codes: [params.code],
                        ActCode: params.actCode,
                    },
                    include: params.include,
                },
                config,
            );

            return contents[0] ?? null;
        },

        /**
         * Обновляет фактическое количество строки состава АИ.
         * @param params данные фактических количеств
         * @param config опции HTTP-запроса
         */
        updateFactQties: async (
            params: {
                data: StockActInvContentUpdateFactQtyData;
            },
            config?: RequestConfig,
        ): Promise<void> => {
            const factQtyByStorePlaces = params.data.factQtyByStorePlaces;

            await stockActInvsApi.content.updateFactQties(
                {
                    qties: [
                        {
                            ContentsCode: params.data.contentCode,
                            FactQty: params.data.factQty,
                            FactQtyByStorePlaces: factQtyByStorePlaces?.map((item) => ({
                                SpCode: item.spCode,
                                FactQty: item.factQty,
                            })),
                        },
                    ],
                },
                config,
            );
        },
    },
};

async function enrichContents(
    contents: ApiStockActInvContentData[],
    include: Array<keyof StockActInvContentExtends>,
    config?: RequestConfig,
): Promise<StockActInvContent[]> {
    const includeDepotCard = include.includes("DepotCard");
    const includeStockObj = include.includes("StockObj");
    const includeMeasure = include.includes("Measure");
    const includeLot = include.includes("Lot");
    const includeOrder = include.includes("Order");
    const includeRemainders = include.includes("PlacedCellRemainders");
    const depotCardCodes = new Set<number>();
    const lotCodes = new Set<number>();
    const orderCodes = new Set<number>();

    contents.forEach((content) => {
        if (includeDepotCard || includeStockObj || includeMeasure) addValidCode(depotCardCodes, content.DepotCode);
        if (includeLot || includeRemainders) addValidCode(lotCodes, content.ParentLotCode);
        if (includeOrder) addValidCode(orderCodes, content.OrderCode);
    });

    const depotCards = new Map<number, ApiStockDepotCard>();
    const stockObjs = new Map<number, StockObj>();
    const measures = new Map<number, ApiMeasure>();
    const lots = new Map<number, ApiStockLot>();
    const orders = new Map<number, ApiOrder>();
    const remainders = new Map<number, StorePlaceRemainder[]>();
    const promises: Promise<unknown>[] = [];

    if (depotCardCodes.size > 0) {
        const depotCardsPromise = stockDepotCardsApi.load({ flt: { Codes: [...depotCardCodes] } }, config);
        promises.push(
            depotCardsPromise.then((items) => {
                items.forEach((item) => depotCards.set(item.Code, item));
            }),
        );
    }

    if (includeLot && lotCodes.size > 0) {
        promises.push(
            lotsServices.load({ flt: { Codes: [...lotCodes] } }, config).then((items) => {
                items.forEach((item) => lots.set(item.LotCode, item));
            }),
        );
    }

    if (includeOrder && orderCodes.size > 0) {
        promises.push(
            ordersApi.load({ flt: { Codes: [...orderCodes] } }, config).then((items) => {
                items.forEach((item) => orders.set(item.Code, item));
            }),
        );
    }

    if (includeRemainders && lotCodes.size > 0) {
        promises.push(
            storePlacesServices.remainders
                .load(
                    {
                        filter: {
                            LotCodes: [...lotCodes],
                            InContextOfLot: true,
                            OnlyPositiveRemainders: true,
                        },
                        include: ["StorePlace"],
                    },
                    config,
                )
                .then((items) => {
                    items.forEach((item) => {
                        if (!isValidCode(item.LotCode)) {
                            return;
                        }

                        const itemsByLot = remainders.get(item.LotCode) ?? [];
                        itemsByLot.push(item);
                        remainders.set(item.LotCode, itemsByLot);
                    });
                }),
        );
    }

    await Promise.all(promises);

    if (includeStockObj || includeMeasure) {
        await enrichDepotCardData(
            [...depotCards.values()],
            { includeStockObj, includeMeasure },
            { stockObjs, measures },
            config,
        );
    }

    return contents.map((content) => {
        const depotCard = depotCards.get(content.DepotCode);

        return serializeContent(content, {
            DepotCard: includeDepotCard ? depotCard : undefined,
            StockObj: includeStockObj && depotCard != null ? stockObjs.get(depotCard.StockobjCode) : undefined,
            Measure: includeMeasure && depotCard != null ? measures.get(depotCard.MeasCode) : undefined,
            Lot: includeLot ? (lots.get(content.ParentLotCode) ?? null) : undefined,
            Order: includeOrder ? (orders.get(content.OrderCode) ?? null) : undefined,
            PlacedCellRemainders: includeRemainders ? remainders.get(content.ParentLotCode) : undefined,
        });
    });
}

async function enrichDepotCardData(
    depotCards: ApiStockDepotCard[],
    include: {
        includeStockObj: boolean;
        includeMeasure: boolean;
    },
    target: {
        stockObjs: Map<number, StockObj>;
        measures: Map<number, ApiMeasure>;
    },
    config?: RequestConfig,
): Promise<void> {
    const stockObjCodes = new Set<number>();
    const measureCodes = new Set<number>();

    depotCards.forEach((card) => {
        if (include.includeStockObj) addValidCode(stockObjCodes, card.StockobjCode);
        if (include.includeMeasure) addValidCode(measureCodes, card.MeasCode);
    });

    const promises: Promise<unknown>[] = [];

    if (stockObjCodes.size > 0) {
        promises.push(
            stockObjsApi.load({ flt: { Codes: [...stockObjCodes] } }, config).then((items) => {
                items.forEach((item) => target.stockObjs.set(item.Code, item));
            }),
        );
    }

    if (measureCodes.size > 0) {
        promises.push(
            measureApi.load({ flt: { Codes: [...measureCodes] } }, config).then((items) => {
                items.forEach((item) => target.measures.set(item.Code, item));
            }),
        );
    }

    await Promise.all(promises);
}

function serializeAct(act: ApiStockActInvData, includeData?: Partial<StockActInvExtends>): StockActInv {
    return {
        ...act,
        Division: includeData?.Division,
        State: includeData?.State,
    };
}

function serializeContent(
    content: ApiStockActInvContentData,
    includeData?: Partial<StockActInvContentExtends>,
): StockActInvContent {
    return {
        ...content,
        DepotCard: includeData?.DepotCard,
        StockObj: includeData?.StockObj,
        Measure: includeData?.Measure,
        Lot: includeData?.Lot,
        Order: includeData?.Order,
        PlacedCellRemainders: includeData?.PlacedCellRemainders,
    };
}

function addValidCode(target: Set<number>, code: number | null | undefined): void {
    if (isValidCode(code)) {
        target.add(code);
    }
}

function isValidCode(code: number | null | undefined): code is number {
    return code != null && code !== 0;
}
